import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ChangeEvent, type Ref } from 'react';
import { toast, type Id } from 'react-toastify';
import type { BodyBowtieConvertImagePost } from '../../api/@types/enhanced-bowtie-api.types';
import {
  useBowtieConvertImageJobIdGetQuery,
  useBowtieConvertImagePostMutation,
} from '../../api/enhanced/enhanced-bowtie-api';
import { useLazyGetSimpleOptionsForFieldQuery } from '../../api/enhanced/enhanced-v4-api';
import { useConfigurationContext } from '../../context/configuration.context';
import { updateToast } from '../../helpers/toast-util';

const maxUploadFileSize = 10485760; // 10 MB
interface ImportBowtieDiagramProps {
  acceptedFileTypes?: string[];
}

const ImportBowtieDiagram = (
  { acceptedFileTypes = ['image/jpg', 'image/jpeg', 'image/png'] }: ImportBowtieDiagramProps,
  ref: Ref<HTMLInputElement>
) => {
  const attachmentRef = useRef<File | undefined>(undefined);
  const infoToastId = useRef<string | undefined>(undefined);
  const [importImageJobId, setImportImageJobId] = useState('');

  const inputRef = useRef<HTMLInputElement | null>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const bowtieConfiguration = useConfigurationContext();
  const [startImportBowtie] = useBowtieConvertImagePostMutation();
  const { data: importImageDetails } = useBowtieConvertImageJobIdGetQuery(
    { jobId: importImageJobId },
    {
      skip: !importImageJobId,
      pollingInterval: importImageJobId ? 5000 : 0,
    }
  );

  const [getControlTypes] = useLazyGetSimpleOptionsForFieldQuery();

  const uploadFile = async () => {
    const file = attachmentRef.current;
    if (!file) {
      return;
    }
    // display loading toast
    infoToastId.current = toast.loading(`Processing ${file.name}`) as string;

    // get control types
    const { controlTypeFieldId, formId } = bowtieConfiguration.controls;
    const controlTypes = await getControlTypes({ fieldId: controlTypeFieldId, formId: formId, limit: -1 }).unwrap();
    const _controlTypes = controlTypes.map((controlType) => controlType.caption!);

    // create a FormData object to send the file as multipart/form-data
    const formData = new FormData();
    formData.append('image_file', file, file.name);
    formData.append('control_types', JSON.stringify(_controlTypes));

    // save job id
    const jobId = await startImportBowtie({
      body: formData as unknown as BodyBowtieConvertImagePost,
    }).unwrap();

    setImportImageJobId(jobId);
  };

  const onFilesChanged = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (!acceptedFileTypes.includes(file.type)) {
        toast.error('Invalid file type, only JPG and PNG files are allowed');
      } else if (file.size > maxUploadFileSize) {
        toast.error('File size exceeds the limit of 10MB');
      } else {
        attachmentRef.current = file;

        try {
          await uploadFile();
        } catch {
          updateToast(infoToastId.current as Id, 'Failed to import bowtie, please try again', 'error');
        }

        attachmentRef.current = undefined;

        // this line below will reset the input to make re-adding same file possible
        event.target.value = '';
      }
    }
  };

  useEffect(() => {
    if (importImageDetails?.status === 'FINISHED' || importImageDetails?.status === 'FAILED') {
      setImportImageJobId('');

      if (importImageDetails?.status === 'FINISHED') {
        // add to local storage
        const diagramId = crypto.randomUUID();
        localStorage.setItem(
          `importedDiagram-${diagramId}`,
          JSON.stringify({
            ...importImageDetails.result,
            bowtieConfiguration: bowtieConfiguration,
          })
        );

        // open imported diagram and reset file upload
        setTimeout(() => {
          window.open(`${window.location.origin}${window.location.pathname}?import=${diagramId}`);
        }, 2500);

        updateToast(infoToastId.current as Id, 'Bowtie data imported successfully', 'success');
      } else {
        updateToast(infoToastId.current as Id, 'Failed to import bowtie, please try again', 'error');
      }
    }
  }, [importImageDetails]);

  return (
    <input
      data-cy="import-bowtie-input"
      ref={inputRef}
      className="bt-hidden"
      type="file"
      accept={acceptedFileTypes.join(',')}
      onChange={onFilesChanged}
    />
  );
};

export default forwardRef(ImportBowtieDiagram);
